import { GlobalEvents } from '@highlight/ipc'
import { SELF_VOICE_MODEL_EVENT } from '@shared/self-voice'
import type { VoiceEmbeddingRequest, VoiceEmbeddingResponse } from './voice-embedding.worker'

/** The voice embedding model, loaded in a worker. Disposing it terminates the worker and frees the model. */
export interface VoiceEmbedder {
  embed(samples: Float32Array): Promise<Array<number>>
  dispose(): void
}

export async function loadVoiceEmbedder(): Promise<VoiceEmbedder> {
  const model = await GlobalEvents.invoke<Uint8Array>(SELF_VOICE_MODEL_EVENT)
  const worker = new Worker(new URL('./voice-embedding.worker.ts', import.meta.url), { type: 'module' })
  const pending = new Map<number, { resolve: (e: Array<number>) => void; reject: (error: Error) => void }>()
  let nextId = 0
  const send = (request: VoiceEmbeddingRequest, transfer: Array<Transferable> = []) =>
    worker.postMessage(request, { transfer })

  await new Promise<void>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<VoiceEmbeddingResponse>) => {
      const response = event.data
      if (response.type === 'loaded') return resolve()
      if (response.type === 'error' && response.id === undefined) return reject(new Error(response.message))
      if (response.id === undefined) return
      const waiter = pending.get(response.id)
      pending.delete(response.id)
      if (response.type === 'embedding') waiter?.resolve(Array.from(response.embedding))
      else waiter?.reject(new Error(response.message))
    }
    worker.onerror = (event) => reject(new Error(`Voice embedding worker failed: ${event.message}`))
    send({ type: 'load', model }, [model.buffer])
  }).catch((error) => {
    worker.terminate()
    throw error
  })

  return {
    embed: (samples) =>
      new Promise((resolve, reject) => {
        const id = nextId++
        pending.set(id, { resolve, reject })
        send({ type: 'embed', id, samples }, [samples.buffer])
      }),
    dispose: () => {
      worker.terminate()
      for (const waiter of pending.values()) waiter.reject(new Error('Voice embedder disposed'))
      pending.clear()
    },
  }
}
